import { z } from 'zod';
import { EnrollmentType } from './enrollment-type.js';
import { MeterDetailSchema } from './meter.js';
// Re-exported so existing importers (and the package index) keep resolving
// `EnrollmentType` from `./address` unchanged, while the definition lives in the
// leaf `enrollment-type` module that breaks the address↔meter import cycle.
export { EnrollmentType };
/**
 * Address → serviceability → ESI resolution contract (E-SF03) — the shape
 * US-Shop's AddressPage sends, and the shape US-Enrollment-BFF's
 * `POST /bff/address/lookup` + `POST /bff/lead` emit.
 *
 * ── WHY THESE LIVE IN THE CONTRACT ─────────────────────────────────────────
 * They are the one edge between the SPA and the BFF (ADR-0002 §4). The BFF uses
 * them as its `response` schema (so the boot-time response-schema guard walks
 * them and the handler validates its own output); US-Shop imports them for typed
 * fetchers. Neither declares `tenantId` or any credential — the BFF derives
 * tenant from Host server-side and never echoes it (AC1c).
 *
 * ── AC3 — NEVER ENUMERABLE ─────────────────────────────────────────────────
 * A candidate carries an OPAQUE, SESSION-SCOPED `candidateId`, NOT the raw
 * ESIID. The ESIID is a 17-digit ERCOT identifier that a broad space could be
 * walked; keeping it server-side (resolved from the funnel session on the next
 * hop) means the browser holds a handle it cannot enumerate and that only its
 * own session can redeem. The premise LABEL it does see is the matched premise
 * address returned by GMO for recognition. With the current simulator's
 * city+zip-only matching this can surface a co-located premise's street the
 * caller did not type; it is a low-sensitivity display aid (rate-limited,
 * session-scoped), never the ESIID. Re-evaluate when a real street-matching GMO
 * lands.
 */
/** Texas retail only (E-SF03 is US-TX). State is fixed server-side; the caller supplies line1/city/zip. */
export const ServiceAddressSchema = z.object({
    line1: z.string().min(1).max(120),
    /** Apartment/unit designator, if any — drives multi-meter disambiguation. */
    unit: z.string().max(40).optional(),
    city: z.string().min(1).max(80),
    /** 5-digit ZIP. Texas premises only; the +4 is not collected. */
    zip: z.string().regex(/^\d{5}$/u, 'ZIP must be 5 digits'),
});
export const AddressLookupRequestSchema = z.object({
    address: ServiceAddressSchema,
    /**
     * The tariff being shopped, if the caller arrived from a plan. When present,
     * the BFF checks the resolved premise's TDU against the plan's TDU and reports
     * `planAvailable`. When absent, `planAvailable` is null (the caller is browsing
     * serviceability, not a specific plan).
     */
    planId: z.string().min(1).max(255).optional(),
});
/**
 * One serviceable premise the caller can pick.
 *
 * The opaque `candidateId` remains the redemption handle for the funnel (the
 * next hops resolve the server-side record from it, never from a browser-sent
 * ESIID). The `esiid` field is a DELIBERATE product decision (2026-07-21): the
 * storefront confirms the customer's OWN resolved meter ("this is your ESI ID")
 * after they typed their exact address — a recognition aid on a premise they
 * already identified, not an enumeration surface. It is display-only; no hop
 * trusts a browser-supplied ESIID.
 */
export const ServiceabilityCandidateSchema = z.object({
    /** Opaque, session-scoped handle. Redeemed to the real ESIID server-side (AC3). */
    candidateId: z.string().min(1),
    /**
     * The resolved 17-digit ERCOT ESIID for this premise, shown to the customer as
     * a confirmation of their own meter. Optional so older/aggregate responses (and
     * out-of-area paths) omit it. Never used as a redemption key — that's `candidateId`.
     */
    esiid: z.string().min(1).max(40).optional(),
    /** The matched premise address, echoed for recognition (the caller's own input). */
    premiseLabel: z.string().min(1),
    /**
     * A short disambiguator for multi-meter premises (a duplex, a unit split):
     * the unit designator or a masked meter hint. Absent for a plain single meter.
     */
    meterHint: z.string().max(60).optional(),
    /** TDU display name for the premise (e.g. "Oncor"). */
    tdsp: z.string().min(1),
    enrollmentType: EnrollmentType,
    /**
     * Whether the shopped plan is available at this premise's TDU. `null` when the
     * request carried no `planId` (the caller was not shopping a specific plan).
     */
    planAvailable: z.boolean().nullable(),
    /**
     * The FULL enriched meter detail for this premise — the SAME shape (and the
     * SAME server-side mapper) that `POST /bff/esiid/lookup` returns for this
     * candidate's ESI ID. Embedding it here makes the ZIP on-ramp a SINGLE call:
     * the SPA renders the meter card straight from `candidate.meter` without a
     * second `/bff/esiid/lookup` hop. OPTIONAL — a candidate whose ESI ID the
     * mapper cannot resolve (e.g. a future real-GMO ESI outside the dev catalog)
     * omits it, and the SPA falls back to the ESI ID lookup. The lighter fields
     * above (esiid, premiseLabel, tdsp, enrollmentType, planAvailable) remain for
     * back-compat and for the disambiguation/label surfaces that do not need the
     * whole meter.
     */
    meter: MeterDetailSchema.optional(),
});
/**
 * The lookup outcome, as a discriminated union so the SPA renders exactly one
 * branch and cannot conflate them:
 *
 *   - `resolved`         — exactly one serviceable premise.
 *   - `ambiguous`        — 2+ premises/meters matched; the caller disambiguates
 *                          (AC1 — ambiguity is a candidate LIST, never a failure).
 *   - `not_found`        — the TDU serves this area but no premise matched the
 *                          address; the caller re-checks their input, and — when
 *                          `newServiceAvailable` — may instead declare a build
 *                          that has no meter yet (E-SF20).
 *   - `out_of_territory` — the address is outside every TDU we serve; the caller
 *                          sees a friendly message + a lead-capture affordance
 *                          (AC2). `leadCaptureAvailable` is always true here so
 *                          the SPA renders the email step.
 *
 * ── WHY NEW CONSTRUCTION IS A FLAG ON `not_found`, NOT ITS OWN OUTCOME ─────
 * The first draft of E-SF20 proposed a fifth member, `new_service_required`. It
 * was wrong, and the reason is worth keeping: WE CANNOT TELL THE TWO APART. An
 * in-territory address that matches no premise is either a typo, an address the
 * TDU has not published yet, or a genuine new build — and GMO returns the same
 * empty list for all three. A distinct outcome would assert a diagnosis we do not
 * have, and would put "tell us about your new build" in front of someone who
 * simply mistyped their street.
 *
 * So the honest shape is one outcome that says what we actually know ("we did not
 * find it here") carrying an affordance that says what is available ("and if it
 * is a new build, there is a path"). The SPA leads with re-check and offers the
 * declaration second — which is also, exactly, the duplicate-declaration guard
 * the epic asks for: the customer is prompted to re-verify their address BEFORE
 * they can declare a build at it.
 */
export const AddressLookupResponseSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('resolved'),
        candidate: ServiceabilityCandidateSchema,
    }),
    z.object({
        outcome: z.literal('ambiguous'),
        candidates: z.array(ServiceabilityCandidateSchema).min(2),
    }),
    z.object({
        outcome: z.literal('not_found'),
        /**
         * Whether the new-service declaration path (E-SF20) is open for this address.
         *
         * REQUIRED, and `z.boolean()` rather than `z.literal(true)` — unlike
         * `leadCaptureAvailable` below, this one genuinely varies. It is false wherever
         * we cannot serve a brand-new premise (a TDU or tenant we have no new-service
         * motion for), and a required field forces every producer to decide rather
         * than inherit an optional default. The server is the only thing that knows;
         * the SPA renders the affordance if and only if it is told to.
         */
        newServiceAvailable: z.boolean(),
    }),
    z.object({
        outcome: z.literal('out_of_territory'),
        leadCaptureAvailable: z.literal(true),
    }),
]);
/**
 * Out-of-area lead capture (AC2). The email is the only new datum; the address
 * is the one the caller already submitted to the lookup. Both are PII on the
 * request path — the BFF redacts them from logs (Pino redaction).
 */
export const LeadCaptureRequestSchema = z.object({
    email: z.string().email().max(254),
    address: ServiceAddressSchema,
});
/**
 * Deliberately minimal: a lead is fire-and-acknowledge. No id is echoed — the
 * anonymous caller has nothing to do with one, and emitting a server-side lead
 * id would be gratuitous surface on a public write.
 */
export const LeadCaptureResponseSchema = z.object({
    status: z.literal('received'),
});
/**
 * ── ADDRESS AUTOCOMPLETE (demo mock) ───────────────────────────────────────
 * `GET /bff/address/suggest?q=<partial>` — a typeahead over serviceable Texas
 * addresses so a storefront visitor PICKS a serviceable premise instead of
 * guessing one of the seeded city/ZIPs. Same PUBLIC posture as the plans shelf:
 * anonymous, credential-free, no tenant, no session.
 *
 * ⚠️ DEMO MOCK, NOT A DIRECTORY. The suggestions are GENERATED dummy street
 * lines over the seeded serviceable city+ZIPs (the GMO simulator matches on
 * city+ZIP, so the street is cosmetic and any suggestion resolves at
 * `/bff/address/lookup`). Production replaces this with a commercial
 * address-autocomplete (Google Places / Smarty) + the real ERCOT/TDU ESIID
 * directory. See STUBS.md.
 *
 * ── AC3 POSTURE — esiid on suggest, but NOT anonymously enumerable ──────────
 * A suggestion carries a display address (`line1/city/state/zip`) + a STABLE,
 * NON-SECRET display `id`, and now an OPTIONAL `esiid`. Carrying the `esiid` here
 * lets the ZIP on-ramp call `/bff/esiid/lookup` DIRECTLY on pick (no
 * `/bff/address/lookup` hop) and makes the ZIP flow fully BFF-local (suggest =
 * mock, esiid/lookup = stub). Because it now egresses esiids, `/bff/address/suggest`
 * is NO LONGER anonymous: it is SESSION-gated (401 without a funnel session) +
 * captcha-gated + rate-limited (5/hour per IP∧session), the SAME sensitive floor as
 * `/bff/address/lookup` — so it CANNOT be scraped as an enumeration oracle (the
 * session requirement is the primary control; the autocomplete-GET captcha caveat
 * is documented in STUBS.md). Still a dummy-flow choice (seeded 5-ZIP mock, no real
 * meters); if suggest ever goes real, drop the esiid and resolve server-side again.
 * No tenant, no candidateId, no other PII rides here.
 */
export const AddressSuggestQuerySchema = z.object({
    /** The partial the user has typed. Case-insensitive substring over "line1, city ZIP". */
    q: z.string().max(120).optional(),
});
/** One suggested, serviceable display address (+ its catalog esiid for the direct lookup). */
export const AddressSuggestionSchema = z.object({
    /** Stable, non-secret display handle (derived from the display address; NOT an ESIID). */
    id: z.string().min(1).max(80),
    line1: z.string().min(1).max(120),
    city: z.string().min(1).max(80),
    /** Texas retail only — the state is fixed. */
    state: z.literal('TX'),
    zip: z.string().regex(/^\d{5}$/u, 'ZIP must be 5 digits'),
    /**
     * The catalog ESI ID for this premise — the ZIP on-ramp feeds it straight to
     * `/bff/esiid/lookup` on pick. OPTIONAL for contract safety (a future/real
     * suggest source may omit it); the demo mock always populates it. ⚠️ See the
     * AC3-relaxation note above — display/lookup input for the dummy flow only.
     */
    esiid: z.string().min(1).max(40).optional(),
});
export const AddressSuggestResponseSchema = z.object({
    /**
     * Up to 25 serviceable suggestions, Oncor city/ZIPs first (they sign cleanly).
     *
     * Raised from 8 alongside the dev premise-catalogue expansion. 8 suited a
     * 15-premise catalogue, but once each ZIP carries 8-10 premises it became the
     * binding constraint: a tester querying one ZIP was only ever offered the same
     * few premises, and Quotation locks an ESI ID as soon as a DRAFT quote exists
     * (409 `US_ESIID_ALREADY_ACTIVE`), so those few burn out within a handful of
     * funnel walks.
     */
    suggestions: z.array(AddressSuggestionSchema).max(25),
});
/**
 * One serviceable ZIP, for the storefront's ZIP picker.
 *
 * DISPLAY DATA ONLY — a ZIP, its city, and how many premises sit behind it.
 * Deliberately carries NO `esiid` and no premise detail: this endpoint answers
 * "where do you serve?", which a REP publishes anyway, and nothing more. That
 * is why it is cheaper to gate than `/bff/address/suggest`, which egresses
 * catalog esiids and is therefore captcha-gated.
 */
export const ServiceableZipSchema = z.object({
    zip: z.string().regex(/^\d{5}$/u),
    city: z.string().min(1).max(80),
    /**
     * Premises available behind this ZIP.
     *
     * NOT rendered in the picker — the dropdown shows the ZIP alone, because the
     * ZIP column is ~180px and a second and third field wrapped into an
     * unreadable list. Kept on the response because it is the number that says
     * whether a ZIP still has unburned meters (Quotation locks an ESI at DRAFT),
     * which is worth having when diagnosing an exhausted pool, and because the
     * route test uses it to prove the picker can never offer a ZIP with nothing
     * behind it.
     */
    premiseCount: z.number().int().min(1),
});
/**
 * `GET /bff/address/zips` — the serviceable city+ZIP list behind the ZIP picker.
 *
 * The cap is 200: the dev catalogue holds 20 city+ZIP pairs, and a real ERCOT
 * footprint is hundreds, not thousands. A larger real dataset must paginate or
 * switch to server-side search rather than raise this — an unbounded list would
 * make the picker unusable long before it became a transfer problem.
 */
export const ServiceableZipsResponseSchema = z.object({
    zips: z.array(ServiceableZipSchema).max(200),
});
//# sourceMappingURL=address.js.map